import React, { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import LanguageSwitcher from './LanguageSwitcher';

const SvgIcon = ({ d, ...props }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
    {typeof d === 'string' ? <path d={d} /> : d}
  </svg>
);

const NAV_ITEMS = [
  // Main
  { path: '/', labelKey: 'nav.dashboard', icon: (
    <SvgIcon d={<><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>} />
  )},
  { path: '/employees', labelKey: 'nav.employees', icon: (
    <SvgIcon d={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></>} />
  )},

  // Separator: Time & Attendance
  { separator: true, labelKey: 'nav.sepTime' },
  { path: '/reports', labelKey: 'nav.reports', icon: (
    <SvgIcon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>} />
  )},
  { path: '/absences', labelKey: 'nav.absences', icon: (
    <SvgIcon d={<><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="10" y1="14" x2="14" y2="18"/><line x1="14" y1="14" x2="10" y2="18"/></>} />
  )},
  { path: '/holidays', labelKey: 'nav.holidays', icon: (
    <SvgIcon d={<><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M12 14l-1.5 3 3-1.5L12 14z"/></>} />
  )},

  // Separator: History
  { separator: true, labelKey: 'nav.sepConfig' },
  { path: '/records', labelKey: 'nav.records', icon: (
    <SvgIcon d={<><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></>} />
  )},
  { path: '/generate', labelKey: 'nav.generate', icon: (
    <SvgIcon d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></>} />
  )},
  { path: '/daily-hours', labelKey: 'nav.dailyHours', icon: (
    <SvgIcon d={<><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><polyline points="12 13 12 16 14 17"/></>} />
  )},
  { path: '/timestamps', labelKey: 'nav.timestamps', icon: (
    <SvgIcon d={<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>} />
  )},
  { path: '/early-auth', labelKey: 'nav.earlyAuth', icon: (
    <SvgIcon d={<><circle cx="12" cy="12" r="10"/><polyline points="12 7 12 12 9 14"/><polyline points="16 11 18 13 22 9"/></>} />
  )},
  { path: '/late-auth', labelKey: 'nav.lateAuth', icon: (
    <SvgIcon d={<><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></>} />
  )},
  { path: '/pto-manual', labelKey: 'nav.ptoManual', icon: (
    <SvgIcon d={<><path d="M12 2v20M2 12h20"/><circle cx="12" cy="12" r="10"/></>} />
  )},
  { path: '/audit-log', labelKey: 'nav.auditLog', adminOnly: true, icon: (
    <SvgIcon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="9" y1="13" x2="15" y2="13"/><line x1="9" y1="17" x2="15" y2="17"/><line x1="9" y1="9" x2="10" y2="9"/></>} />
  )},
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [clock, setClock] = useState(new Date());
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const timer = setInterval(() => setClock(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) setSidebarOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleLogout = () => {
    Swal.fire({
      title: t('nav.logout'),
      text: t('nav.logoutConfirm'),
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: t('nav.logoutYes'),
      cancelButtonText: t('common.cancel'),
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        logout();
        navigate('/login');
      }
    });
  };

  const formatDate = (d) => d.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const formatTime = (d) => d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit' });

  const closeSidebarOnMobile = () => {
    if (isMobile) setSidebarOpen(false);
  };

  const currentPage = NAV_ITEMS.find(item => !item.separator && (
    item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path)
  ));
  const pageTitle = currentPage ? t(currentPage.labelKey) : '';

  return (
    <div className="layout">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img
              src={require('../assets/images/TMS logo stack vertical white-02.png')}
              alt="TMS Logo"
              className="sidebar-logo-img"
            />
          </div>
          {!isMobile && (
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>
              {sidebarOpen ? '\u2039' : '\u203A'}
            </button>
          )}
          {isMobile && sidebarOpen && (
            <button className="sidebar-toggle" onClick={() => setSidebarOpen(false)}>
              ✕
            </button>
          )}
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.filter(item => !item.adminOnly || isAdmin).map((item, i) =>
            item.separator ? (
              sidebarOpen && (
                <div key={`sep-${i}`} className="nav-separator">
                  <span>{t(item.labelKey)}</span>
                </div>
              )
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                onClick={closeSidebarOnMobile}
              >
                <span className="nav-icon">{item.icon}</span>
                {sidebarOpen && <span className="nav-label">{t(item.labelKey)}</span>}
              </NavLink>
            )
          )}
        </nav>

        <div className="sidebar-footer">
          <button className="btn-logout" onClick={handleLogout}>
            <span className="nav-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </span>
            {sidebarOpen && <span>{t('nav.logout')}</span>}
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <div className="topbar-left">
            {isMobile && (
              <button className="hamburger-btn" onClick={() => setSidebarOpen(true)}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <line x1="3" y1="6" x2="21" y2="6"/>
                  <line x1="3" y1="12" x2="21" y2="12"/>
                  <line x1="3" y1="18" x2="21" y2="18"/>
                </svg>
              </button>
            )}
          </div>
          <div className="topbar-right">
            <div className="topbar-clock">
              <div className="clock-time">{formatTime(clock)}</div>
              <div className="clock-date">{formatDate(clock)}</div>
            </div>
            <div className="topbar-divider" />
            <LanguageSwitcher style={{ color: 'var(--text-secondary)' }} />
            <div className="topbar-divider" />
            <div className="topbar-user">
              <span className="user-avatar">{(user?.username || 'U')[0].toUpperCase()}</span>
              <span className="user-name">{user?.username}</span>
            </div>
          </div>
        </header>

        <main className="page-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
